import styles from "./Breadcumbs.module.scss";
import Link from '@/components/AppLink';
import { usePathname } from '@/lib/router-navigation';
import { Flex } from "@/components";
import { SIDEBAR_NAV_ITEMS } from "@/constants/sidebarNavItems.const";
import { Fragment, useMemo, type ReactNode } from "react";
import { ChevronRightIcon } from "@vetty/icons";

const BreadcumbsSeparator = () => (
  <div className={styles["comp__separator"]}>
    <ChevronRightIcon style={{ width: 20, height: 20 }} />
  </div>
);

type BreadcrumbItem = {
  label: string;
  icon?: ReactNode;
  href?: string;
};

const Breadcumbs = () => {
  const pathname = usePathname();
  const currentBreadcumbs = useMemo(() => {
    const currentPath = pathname ?? "/";

    for (const group of SIDEBAR_NAV_ITEMS) {
      for (const item of group.items) {
        if (currentPath === item.href) {
          return [
            { label: group.group },
            { label: item.label, icon: item.icon, href: item.href },
          ] satisfies BreadcrumbItem[];
        }
      }

      for (const item of group.items) {
        if (item.subItems && currentPath.startsWith(item.href + "/")) {
          const matchedSubItem = item.subItems.find((subItem) =>
            subItem.exactActive
              ? currentPath === subItem.href
              : currentPath === subItem.href ||
                currentPath.startsWith(subItem.href + "/")
          );

          if (matchedSubItem) {
            return [
              { label: group.group },
              { label: item.label, icon: item.icon, href: item.href },
              {
                label: matchedSubItem.label,
                icon: matchedSubItem.icon,
                href: matchedSubItem.href,
              },
            ] satisfies BreadcrumbItem[];
          }

          return [
            { label: group.group },
            { label: item.label, icon: item.icon, href: item.href },
          ] satisfies BreadcrumbItem[];
        }
      }

      const flatPrefixCandidates = group.items.filter(
        (item) =>
          !item.subItems &&
          item.href.length > 0 &&
          currentPath.startsWith(item.href + "/")
      );
      if (flatPrefixCandidates.length > 0) {
        const item = flatPrefixCandidates.reduce((a, b) =>
          a.href.length >= b.href.length ? a : b
        );
        return [
          { label: group.group },
          { label: item.label, icon: item.icon, href: item.href },
        ] satisfies BreadcrumbItem[];
      }
    }

    return [
      { label: "General" },
      { label: "Overview" },
    ] satisfies BreadcrumbItem[];
  }, [pathname]);

  return (
    <Flex className={styles["comp"]} align={"center"} gap={12}>
      {currentBreadcumbs.map((item, index) => (
        <Fragment key={`${item.label}-${index}`}>
          {index > 0 && <BreadcumbsSeparator />}
          {item.href && index < currentBreadcumbs.length - 1 ? (
            <Link href={item.href} className={styles["comp__item-link"]}>
              <div className={styles["comp__item"]}>
                {item.icon ? (
                  <div className={styles["comp__item-content"]}>
                    <div className={styles["comp__item-icon"]}>{item.icon}</div>
                    <span>{item.label}</span>
                  </div>
                ) : (
                  item.label
                )}
              </div>
            </Link>
          ) : (
            <div
              className={`${styles["comp__item"]} ${
                index === currentBreadcumbs.length - 1
                  ? styles["comp__item--active"]
                  : ""
              }`}
            >
              {item.icon ? (
                <div className={styles["comp__item-content"]}>
                  <div className={styles["comp__item-icon"]}>{item.icon}</div>
                  <span>{item.label}</span>
                </div>
              ) : (
                item.label
              )}
            </div>
          )}
        </Fragment>
      ))}
    </Flex>
  );
};

export { Breadcumbs };
